import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Plus,
  Users,
  User,
  Calendar,
  X,
  BarChart3,
  FileText,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { TeamPerformanceReport } from '../components/reports/TeamPerformanceReport';

const CATEGORIES = ['ACADEMY', 'FIRST_TEAM', 'B_TEAM', 'WOMEN', 'OTHER'];
const AGE_GROUPS = ['U14', 'U15', 'U16', 'U17', 'U19', 'SENIOR'];
const STAFF_ROLES = ['HEAD_SNC', 'SNC', 'HEAD_PHYSIO', 'PHYSIO', 'HEAD_COACH', 'COACH', 'ASSISTANT'];

const StatusBadge = ({ status }) => {
  const statusClasses = {
    FULLTRAIN: 'status-fulltrain',
    RTP: 'status-rtp',
    OUT: 'status-out',
  };
  return (
    <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${statusClasses[status] || ''}`}>
      {status === 'FULLTRAIN' ? 'FIT' : status}
    </span>
  );
};

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const { t, language } = useLanguage();

  const [team, setTeam] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [allStaffUsers, setAllStaffUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Staff assignment dialog
  const [showAssignStaffDialog, setShowAssignStaffDialog] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignRole, setAssignRole] = useState('SNC');
  const [isAssigning, setIsAssigning] = useState(false);

  // Delete dialog
  const [showTeamReport, setShowTeamReport] = useState(false);

  // Delete dialog

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTeam = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/teams/${teamId}`);
      setTeam(response.data);
      setEditData(response.data);
    } catch (err) {
      setError('Failed to load team');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [api, teamId]);

  const fetchAthletes = useCallback(async () => {
    try {
      const response = await api.get(`/athletes?team_id=${teamId}`);
      setAthletes(response.data);
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  }, [api, teamId]);

  const fetchStaff = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/staff-teams', { params: { team_id: teamId } });
      setStaff(response.data);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
    }
  }, [api, teamId, isAdmin]);

  const fetchAllStaffUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/users');
      setAllStaffUsers(response.data.filter(u => u.is_active && ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST'].includes(u.role)));
    } catch (err) {
      console.error('Failed to fetch staff users:', err);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    fetchTeam();
    fetchAthletes();
    fetchStaff();
    fetchAllStaffUsers();
  }, [fetchTeam, fetchAthletes, fetchStaff, fetchAllStaffUsers]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/teams/${teamId}`, {
        name: editData.name,
        description: editData.description,
        age_group: editData.age_group,
        category: editData.category,
        season: editData.season,
      });
      toast.success(t('saveSuccess'));
      setIsEditing(false);
      fetchTeam();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!assignUserId) return;
    setIsAssigning(true);
    try {
      await api.post('/staff-teams', {
        user_id: assignUserId,
        team_id: teamId,
        role_in_team: assignRole,
      });
      toast.success(t('saveSuccess'));
      setShowAssignStaffDialog(false);
      setAssignUserId('');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveStaff = async (staffTeamId) => {
    try {
      await api.delete(`/staff-teams/${staffTeamId}`);
      toast.success(t('deleteSuccess'));
      fetchStaff();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/teams/${teamId}`);
      toast.success(t('deleteSuccess'));
      navigate('/staff/roster/teams');
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      ACADEMY: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      FIRST_TEAM: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      B_TEAM: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
      WOMEN: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
      OTHER: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold uppercase rounded-sm border ${colors[category] || colors.OTHER}`}>
        {category}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="text-center py-12">
        <p className="text-rose-400">{error || 'Team not found'}</p>
        <Button
          onClick={() => navigate('/staff/roster/teams')}
          variant="outline"
          className="mt-4 border-zinc-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teams
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {showTeamReport && <TeamPerformanceReport teamId={teamId} teamName={team.name} onClose={() => setShowTeamReport(false)} />}
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <Button
          onClick={() => navigate('/staff/roster/teams')}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-zinc-100"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-100">{team.name}</h1>
            {getCategoryBadge(team.category)}
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            <span className="font-mono text-amber-500">{team.code}</span> • {team.age_group} • {team.season}
          </p>
        </div>
        {isAdmin && (
          <div className="flex w-full lg:w-auto min-w-0 items-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate(`/staff/team/${teamId}/season`)}
              variant="outline"
              className="border-cyan-700 text-cyan-200 hover:bg-cyan-900/30"
              data-testid="team-season-analytics-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {t ? t('Season Analytics') : 'Season Analytics'}
            </Button>
            <Button
              onClick={() => setShowTeamReport(true)}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="team-performance-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {t ? t('Team Performance Report') : 'Team Performance Report'}
            </Button>
            {!isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="edit-team-button"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {t('edit')}
              </Button>
            )}
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="border-rose-900/50 text-rose-400 hover:bg-rose-900/20"
              data-testid="delete-team-button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Info */}
        <div className="lg:col-span-2 min-w-0 space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">
              {isEditing ? t('editTeam') : 'Team Information'}
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('name')}</Label>
                    <Input
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-team-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('season')}</Label>
                    <Input
                      value={editData.season || ''}
                      onChange={(e) => setEditData({ ...editData, season: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-team-season"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('ageGroup')}</Label>
                    <Select
                      value={editData.age_group || ''}
                      onValueChange={(val) => setEditData({ ...editData, age_group: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-age-group">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {AGE_GROUPS.map((ag) => (
                          <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('category')}</Label>
                    <Select
                      value={editData.category || ''}
                      onValueChange={(val) => setEditData({ ...editData, category: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('description')}</Label>
                  <Textarea
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                    data-testid="edit-description"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button
                    onClick={() => { setIsEditing(false); setEditData(team); }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                    data-testid="save-team-button"
                  >
                    {isSaving ? t('loading') : t('save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {team.description && (
                  <p className="text-zinc-300 mb-4">{team.description}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-4 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center">
                    <div className="text-3xl font-bold text-amber-500">{athletes.length}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{t('athletes')}</div>
                  </div>
                  <div className="p-4 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center">
                    <div className="text-3xl font-bold text-emerald-500">
                      {athletes.filter(a => a.status === 'FULLTRAIN').length}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Fit</div>
                  </div>
                  <div className="p-4 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center">
                    <div className="text-3xl font-bold text-rose-500">
                      {athletes.filter(a => a.status === 'OUT' || a.status === 'RTP').length}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Unavailable</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Athletes in Team */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm">
            <div className="p-4 border-b border-zinc-800">
              <h3 className="font-semibold text-zinc-100">{t('athletes')} ({athletes.length})</h3>
            </div>
            {athletes.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">No athletes in this team</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('name')}</th>
                      <th>{t('position')}</th>
                      <th>#</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {athletes.map((athlete) => (
                      <tr key={athlete.id}>
                        <td>
                          <Link
                            to={`/staff/roster/${athlete.id}`}
                            className="flex items-center gap-3 hover:text-amber-500 transition-colors"
                          >
                            <div className="w-8 h-8 bg-zinc-800 rounded-sm flex items-center justify-center text-zinc-500">
                              <User className="w-4 h-4" />
                            </div>
                            <span className="font-medium">{athlete.first_name} {athlete.last_name}</span>
                          </Link>
                        </td>
                        <td>
                          <span className="font-mono text-amber-500">{athlete.primary_position}</span>
                        </td>
                        <td>
                          <span className="font-mono text-zinc-400">{athlete.jersey_number || '-'}</span>
                        </td>
                        <td>
                          <StatusBadge status={athlete.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Staff Assignments (Admin only) */}
        {isAdmin && (
          <div className="space-y-6">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-zinc-100">{t('staffAssignments')}</h2>
                <Button
                  onClick={() => setShowAssignStaffDialog(true)}
                  size="sm"
                  className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                  data-testid="assign-staff-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {staff.length === 0 ? (
                  <p className="text-sm text-zinc-500">No staff assigned</p>
                ) : (
                  staff.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm"
                    >
                      <div>
                        <div className="font-medium text-zinc-100">{s.user_name}</div>
                        <div className="text-xs text-zinc-500">{s.role_in_team}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveStaff(s.id)}
                        className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors"
                        data-testid={`remove-staff-${s.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assign Staff Dialog */}
      <Dialog open={showAssignStaffDialog} onOpenChange={setShowAssignStaffDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('assignStaff')}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Assign a staff member to this team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Staff Member</Label>
              <Select value={assignUserId} onValueChange={setAssignUserId}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-staff-user">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {allStaffUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Role in Team</Label>
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {STAFF_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAssignStaffDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleAssignStaff}
              disabled={isAssigning || !assignUserId}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
              data-testid="confirm-assign-staff-button"
            >
              {isAssigning ? t('loading') : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">Delete Team</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t('confirmDelete')} This will deactivate the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 text-white hover:bg-rose-500"
              data-testid="confirm-delete-team-button"
            >
              {isDeleting ? t('loading') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamDetailPage;
